import React from 'react';
import {
  Box,
  Toolbar,
  Tooltip,
  IconButton,
  Grid,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Collapse
} from '@mui/material';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { useDispatch } from 'react-redux';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import { getBankBranchData, setBranchDetails } from '../../store/Slice/Global/GlobalSlice';

export default function ReceivableInvoiceToolbar({
  setFilterParameter,
  isOpenFilter,
  setIsOpenFilter
}) {
  const [localFilters, setLocalFilters] = React.useState({
    stateId: null,
    bankName: null,
    branchName: null,
    status: null,
    fromDate: null,
    toDate: null
  });

  const dispatch = useDispatch();
  const { stateDrop, BankDrop, BankBranchDrop } = useGlobalSelector();

  React.useEffect(() => {
    if (typeof setFilterParameter !== 'function') return;
    setFilterParameter({
      stateId: null,
      bankName: null,
      branchName: null,
      status: null,
      fromDate: null,
      toDate: null
    });
  }, [setFilterParameter]);

  const handleFilterChange = (key, value) => {
    const updated = { ...localFilters, [key]: value };
    setLocalFilters(updated);
    setFilterParameter(updated);
  };

  const handleResetFilters = () => {
    const reset = {
      stateId: null,
      bankName: null,
      branchName: null,
      status: null,
      fromDate: null,
      toDate: null
    };
    setLocalFilters(reset);
    setFilterParameter(reset);
  };

  return (
    <Box sx={{ width: '100%', bgcolor: '#ffffff' }}>
      <Collapse in={Boolean(isOpenFilter)}>
        <Box
          sx={{
            px: 3,
            py: 2.5,
            bgcolor: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            borderBottom: '1px solid #e2e8f0',
            mx: 2,
            my: 2,
            borderRadius: '12px'
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <Select
                fullWidth
                size="small"
                displayEmpty
                value={localFilters.stateId ?? ''}
                onChange={(e) =>
                  handleFilterChange('stateId', e.target.value === '' ? null : e.target.value)
                }
                sx={{
                  borderRadius: '8px',
                  bgcolor: '#ffffff',
                  fontSize: 13,
                  '& fieldset': { borderColor: '#e2e8f0' }
                }}
              >
                <MenuItem value="">All States</MenuItem>
                {stateDrop?.map((item) => (
                  <MenuItem key={item?.value} value={item?.value}>
                    {item?.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={2.2}>
              <Autocomplete
                disablePortal
                id="combo-box-invoice-bank"
                options={BankDrop || []}
                size="small"
                value={BankDrop?.find((opt) => opt.label === localFilters.bankName) || null}
                getOptionLabel={(option) => option?.label || ''}
                onChange={(e, newValue) => {
                  const updated = {
                    ...localFilters,
                    bankName: newValue?.label || null,
                    branchName: null
                  };
                  setLocalFilters(updated);
                  setFilterParameter(updated);
                  dispatch(setBranchDetails([]));
                  if (newValue?.value) {
                    dispatch(getBankBranchData(newValue.value));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Bank"
                    sx={{
                      bgcolor: '#ffffff',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: 13,
                        '& fieldset': { borderColor: '#e2e8f0' }
                      }
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.2}>
              <Autocomplete
                disablePortal
                id="combo-box-invoice-branch"
                options={BankBranchDrop || []}
                size="small"
                value={BankBranchDrop?.find((opt) => opt.label === localFilters.branchName) || null}
                getOptionLabel={(option) => option?.label || ''}
                onChange={(e, newValue) => {
                  handleFilterChange('branchName', newValue?.label || null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Branch"
                    sx={{
                      bgcolor: '#ffffff',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: 13,
                        '& fieldset': { borderColor: '#e2e8f0' }
                      }
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={1.8}>
              <Select
                fullWidth
                size="small"
                displayEmpty
                value={localFilters.status ?? ''}
                onChange={(e) =>
                  handleFilterChange('status', e.target.value === '' ? null : e.target.value)
                }
                sx={{
                  borderRadius: '8px',
                  bgcolor: '#ffffff',
                  fontSize: 13,
                  '& fieldset': { borderColor: '#e2e8f0' }
                }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="PAID">Paid</MenuItem>
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={1.8}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                InputLabelProps={{ shrink: true }}
                value={localFilters.fromDate || ''}
                onChange={(e) => handleFilterChange('fromDate', e.target.value || null)}
                sx={{
                  bgcolor: '#ffffff',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: 13,
                    '& fieldset': { borderColor: '#e2e8f0' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={1.8}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                InputLabelProps={{ shrink: true }}
                value={localFilters.toDate || ''}
                onChange={(e) => handleFilterChange('toDate', e.target.value || null)}
                sx={{
                  bgcolor: '#ffffff',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: 13,
                    '& fieldset': { borderColor: '#e2e8f0' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm="auto">
              <Tooltip title="Reset Filters">
                <IconButton
                  onClick={handleResetFilters}
                  sx={{
                    color: '#dc2626',
                    bgcolor: '#fee2e2',
                    borderRadius: '8px',
                    p: 1,
                    '&:hover': { bgcolor: '#fecaca' }
                  }}
                >
                  <FilterListOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
}
